/*!
 *  (c)(r) 2008-2021 IT-Service Robert Frunzke
 */

import { sprintf } from '../vendor/sprintf.js';
import { arr_inArray, elementHasClassName, htmlspecialchars, split_once, str_trim, x_split } from './util.js';
import { mj } from './page-mj.js';
import { jQuery } from '../other/jquery-hooked.js';
import { mjt } from './i18ntexts.js';

import { MDLocale } from './locale-impl.js';

import { MDMeta } from './meta-data.js';
import { convertToSelect2WithDefaultFit } from './select2.js';
import { MVTranslationsEditor } from './translations-editor.js';
import { mjWidgetOverlayEditor } from './widget-overlay-editor.js';


/**
 * Manja Widgets ( .mjWidget )
 *
 * common widget options:
 *  - type						: mj_widget_type
 *  - name						: string
 *  - values					: [ [value,label], [value,label], ... ]
 *  - default					: any
 *  - addClass					: string - css class to add to actual input element
 *  - change					: called when widgets value has changed (after user interaction done),
 *  - prompt_change				: like "change", but may be called when user is interacting with widget (e.g. when typing into text box),
 * 									NOTE: "prompt_change" overrides "change", means: when "prompt_change" option exists, then "change" will never be called at all.
 * 									ALSO: there is an option "internal_prompt_change", which acts like exactly like "prompt_change", but does not override "change".
 *  - ctrl_enter_submits_form	: bool (for textarea and text input widgets only)
 *
 *
 *
 * options for diverse types:
 *  - setup_cb					: function() - called after data was loaded and an actual element or widget was added to dom
 *
 * options for int, float & special-float:
 *  - no_negative_values	: bool
 *
 * other options:
 *  - unit_label	: (for int, float, special-float)
 *
 * options for special-float:
 *  - unit_label_addClass	: string - css class to add to unit label element
 *  - accepted_prefixes		: string - list of accepted prefix characters in value
 *
 * options for string-list:
 *  - remove_whitespace_and_empty_items	: bool
 *
 * options for int-vector & float-vector
 *  - nelements		: int
 *  - labels		: [ label0, label1, label2, ... ]
 *  - unit_labels	: [ label0, label1, label2, ... ]
 *  - convert_from_to_string : bool
 *
 * options for dyna-set:
 *  - edit_label	: string (link text, editor opens on click on this text)
 *  - get_values
 *
 * options for set:
 *  - checkboxGroupClass: string (default "checkbox-group");
 *
 * options for tree-set:
 *  - check_selectable_flag	: bool (if set, then fourth value in values row is "selectable"; if true then show checkbox, otherwise do not show it [entry becomes label only])
 *  - checkboxGroupClass: string (default "checkbox-group");
 *
 * options for ml-text:
 *  - css 		: css attribute object
 *  - ctype		: content type - used to set text editor mode, usually one of either 'text/plain' or 'text/html'
 *
 * options for user, group, user_list, and group_list
 *  - flt_role	: a manja role name, users or groups should be filtered on
 *
 * options for meta-field
 *  - flt_cb	: function, which is called for each meta data definition. must return true to include it, return false to exclude that definition.
 *
 * additional options for user, group, user_list, group_list, category and category_list
 *  - show_ids	: whether to display user ids, group ids or category ids in selection lists
 *
 * additional options for group_or_user
 *  - add_empty_default : bool - whether to add an empty entry for default case
 *
 * additional options for list
 *  - html_labels			: bool - use the labels (from values) directly, do not html escape them
 *  - allow_disabled_values	: bool - allow values with third entry of bool type, that sets items disabled state, e.g. [ [value,label,disabled], [value,label,disabled], ... ]
 *
 * additional options for string and text
 *  - placeholder	: string to display as a placeholder value
 *
 * additional options for string
 * - restrictions	: object with one or more of: maxlength, minlength, pattern, inputmode
 *
 * additional options for set, tree-set, bool2
 *  - checkboxGroupClass: string (default "checkbox-group");
 *
 * additional options for radio_list, bool, bool3
 *  - radioGroupClass: string (default "radio-group");
 *
 *
 * enum mj_widget_type:
 *
 *  - string				single-line text							html: input type=text
 *  - text					multi-line text 							html: textarea
 *  - list					list of values								html: select
 *  - radio_list			list of values								html: list of radioboxes
 *  - set					multiple choice								html: list of checkboxes
 *  - map					list of key/value pairs	with unique keys	html: list of pairs of either (input type=text + select) or (input type=text + input type=text) or (select + select)
 *  - bool					yes/no										html: radio buttons for yes & no
 *  - bool2					yes/no										html: single checkbox
 *  - bool3					yes/no/undefined							html: radio buttons for yes, no & undefined
 *  - download_formats		list of dl fmts								html: list of select
 *  - date					date selection								html: jquery datepicker
 *  - datetime				date+time selection							html: jquery datepicker + text input for time
 *  - interval				date+time interval							html: select (currently this is a plain <select> containing a list of common intervals)
 *  - group_list			list of groups								html: custom
 *  - user_list				list of users								html: custom
 *  - user					single user									html: select
 *  - group					single group								html: select
 *  - category				single category								html: select
 *  - category_list			list of categories							html: custom
 *
 *  - group_or_user			single group or user 						html: select
 *
 *  - ml-string				multilingual single-line text				html: input type=text + multilingual input method (using overlay widget)
 *  - ml-text				multilingual multi-line text				html: textarea + multilingual input method (using overlay widget)
 *
 *  - ml-string-2			multilingual single-line text				html: input type=text + multilingual input method (using inline widget)
 *
 *  - string-list			list of single-line text items				html: list of input type=text
 *  - int					rational number								html: input type=text
 *  - float					floating point number, localized			html: input type=text
 *  - special-float			float with additional options				html: input type=text
 *  - int-vector			fixed-length array of integers				html: list of <input type=text> elements (required options: nelements; optional: labels, unit_labels)
 *  - float-vector			fixed-length array of floats				html: list of <input type=text> elements (required options: nelements; optional: labels, unit_labels)
 *
 *  - dyna-set				multiple choice								html: link to "list of checkboxes" in overlay widget; in contrast to "set" widget, the list of available choices will be loaded on edit, not on init
 *
 *  - tree-set				multiple choice								html: list of checkboxes, indented by hierarchy depth etc...
 *
 *  - click2edit-text		multi-line text, activated on click			html: textarea or clickable <span>
 *
 *  - meta-field			select one from the metadata fields			html: select
 *
 *  - string-label			single-line text, but editing disabled		html: span
 *
 *
 * methods:
 *  - init(opts)
 *  - set(value)		: set a value
 *  - get()				: get current value
 *  - focus()			: set focus on element
 *  - select()			: select content of element
 *  - id()				: get id of html input element
 *  - focus_id()		: get id of html input element or (for some widget types) the id of first contained html input element (e.g. the element to which label elements "for" attribute should point to)
 *  - name()			: get name of widget (as provided in setup)
 *
 *
 * usage:
 *  $('#container').mjWidget( { type:'string', name:'name', default:'value' } )
 *  $('#container').mjWidget( 'set', 'value' )
 *  var value = $('#container').mjWidget( 'get' )
 */
(function($,mj) {

	var _mj_wdg_id_counter = 0;
	var _mj_wdg_elc = {};

	function _mj_wdg_id() {
		_mj_wdg_id_counter++;
		return 'mj_wdg_'+_mj_wdg_id_counter;
	}

	function _mj_wdg_clean_id_list( arr ) {
		var i,a,v, ret=[];
		if( Array.isArray(arr) ) {
			for( i=0, a=arr.length; i<a; ++i ) {
				v = arr[i];
				if( typeof v === 'number' ) ret.push(v);
				else ret.push(parseInt(v,10));
			}
		}
		return ret;
	}

	function _mj_wdg_check_add_change( self, opts, $elem, skip_setup_cb ) {
		if( opts.setup_cb && !skip_setup_cb ) opts.setup_cb.call(self);
		var tagName = $elem.length ? $elem[0].tagName : 'UNKNOWN';
		if( tagName==='SPAN' ) return $elem;
		if( opts.change || opts.prompt_change || opts.internal_prompt_change ) {
			$elem.on('change.mjWidget',function(e) {
				if( opts.internal_prompt_change ) opts.internal_prompt_change.call(self,$elem);
				if( opts.prompt_change ) opts.prompt_change.call(self);
				else if( opts.change ) opts.change.call(self);
			} );

			if( opts.prompt_change || opts.internal_prompt_change ) {
				$elem.on('onpaste.mjWidget',function(e) {
					if( opts.internal_prompt_change ) opts.internal_prompt_change.call(self,$elem);
					if( opts.prompt_change ) opts.prompt_change.call(self);
				} );
				$elem.on('keyup.mjWidget',function(e) {
					var k = e.keyCode;
					if(	k!==  9 &&		// TAB
						k!== 16 &&		// SHIFT
						k!== 17 &&		// CONTROL
						k!== 18 &&		// ALT
						k!==224 &&		// META
						k!== 27 && 		// ESCAPE
						k!== 91 &&		// OS (e.g. windows key)
						k!== 93 &&		// CONTEXT MENU
						!(k>=33&&k<=40)	// PAGEUP, PAGEDOWN, END, HOME, ARROW LEFT, ARROW UP, ARROW RIGHT, ARROW DOWN
					) {
						if( opts.internal_prompt_change ) opts.internal_prompt_change.call(self,$elem);
						if( opts.prompt_change ) opts.prompt_change.call(self);
					}
				} );
			}
		}
		if( opts.ctrl_enter_submits_form ) {
			if( tagName==='INPUT' || tagName==='TEXTAREA' ) {
				$elem.on('keydown.mjWidget',function(e) {
					if( e.keyCode==13 && (e.ctrlKey||e.metaKey||e.shiftKey) ) {
						$(e.target).closest('form').trigger('submit');
						e.preventDefault();
						return false;
					}
				} );
			}
		}
		return $elem;
	}

	function _mj_wdg_build_select_options( values, html_labels, allow_disabled_values ) {
		if( typeof values === 'string' ) {
			let tmpl = document.createElement('template');
			tmpl.innerHTML = values;
			return tmpl.content; // DocumentFragment
		}
		if( Array.isArray(values) ) {
			let doc_frag = document.createDocumentFragment();
			var i,v,css_key,title;
			var vl = values.length;
			for( i=0; i<vl; ++i ) {
				v = values[i];
				if( !Array.isArray(v) ) v = [v,v];

				var option_elem = document.createElement('option');
				option_elem.value = v[0];

				if( allow_disabled_values && ( v.length>2 ? !!v[2] : false ) ) option_elem.disabled = true;

				if( v.length>3 && v[3] && (typeof v[3] === 'object') ) {
					for( css_key in v[3] ) {
						if( v[3].hasOwnProperty(css_key) ) option_elem.style[css_key] = v[3][css_key];
					}
				}
				if( v.length>4 && v[4] ) option_elem.dataset.parent = v[4];

				if( v.length>5 && v[5] ) {
					if( Array.isArray(v[5]) ) option_elem.className = v[5].join(' ');
					else option_elem.className = v[5];
				}

				title = v[1];
				if( html_labels ) {
					if( title.includes('&') || title.includes('<') ) option_elem.innerHTML = title;
					else option_elem.textContent = title;
				} else {
					option_elem.textContent = title;
				}

				doc_frag.appendChild(option_elem);
			}
			return doc_frag;
		}
		throw new Error('invalid type of "values": '+(typeof values));
	}


	function _mj_wdg_create_entity_select( wdg_elem, name, id, entities, value, exclude_items, hide_exclusions, opt_show_ids, select_cb, select_opts ) {
		var values = []; // [ [value,label], [value,label], ... ]
		var i, entity, exclude_item, label;
		var eal = entities.length;
		if( exclude_items.length===0 ) {
			for( i=0; i<eal; ++i ) {
				entity = entities[i];
				label = htmlspecialchars(entity.title);
				if( opt_show_ids ) label += ' (ID '+htmlspecialchars(entity.id)+')';
				values.push( [
					entity.id,
					label,
					false,
					entity.css,
					entity.parent || false,
				] );
			}
		} else {
			for( i=0; i<eal; ++i ) {
				entity = entities[i];
				exclude_item = exclude_items.length===0 ? false : exclude_items.indexOf(entity.id)!==-1;
				if( !exclude_item || (exclude_item && !hide_exclusions) ) {
					label = htmlspecialchars(entity.title);
					if( opt_show_ids ) label += ' (ID '+htmlspecialchars(entity.id)+')';
					values.push( [
						entity.id,
						label,
						exclude_item,
						entity.css,
						entity.parent || false,
					] );
				}
			}
		}

		var wdg_opts = Object.assign( {
			type: 'list',
			name: name,
			id: id,
			values: values,
			html_labels: true,
			allow_disabled_values: true,
			value: value,
			change: function change(e) {
				if( select_cb ) select_cb( wdg_elem.mjWidget('get') );
			},
		}, select_opts||{} );
		wdg_elem.empty().mjWidget(wdg_opts);
	}

	function _mj_query_entity_list_from_load_cb( cache_key, query_data_cb, data_ready_cb ) {
		if( _mj_wdg_elc.hasOwnProperty(cache_key) ) {
			// check queue & cache ...
			var elc = _mj_wdg_elc[cache_key];
			if( elc.loading ) {
				// data loading is in progress -> queue up data_ready_cb ...
				elc.cbs.push(data_ready_cb);
			} else {
				// data is already available -> call data_ready_cb directly ...
				data_ready_cb(elc.data);
			}
			return;
		}
		// create cache entry ...
		_mj_wdg_elc[cache_key] = {
			loading: true,
			cbs: [data_ready_cb],
			data: null,
		};
		query_data_cb(
			function _on_query_success( data ) {
				var elc = _mj_wdg_elc[cache_key];
				elc.loading = false;
				elc.data = data;
				// call all data_ready_cb() closures which were queued for cache_key
				for( var i=0; i<elc.cbs.length; ++i ) {
					(elc.cbs[i])(elc.data);
				}
				// release references to queued closures
				elc.cbs = [];
			}
		);
	}

	function _mj_query_entity_list_from_url( url, query, data_ready_cb ) {
		var queryString = query ? ( typeof query === 'string' ? query : $.param(query) ) : '';
		_mj_query_entity_list_from_load_cb(
			url+'?'+queryString,
			function _on_query_data( query_success_cb ) {
				$.postJSON( url, queryString, function( data ) {
					if( Array.isArray(data) ) query_success_cb( { entities: data } );
					else query_success_cb( data );
				} );
			},
			data_ready_cb
		);
	}

	function _mj_query_entity_list_by_opts( opts, data_ready_cb ) {
		var es = opts.entity_source || { };
		var es_type = typeof es;
		if( es_type === 'string' ) es = { url: es };
		else if( es_type === 'function' ) es = { query_data_cb: es };
		if( es.url ) {
			_mj_query_entity_list_from_url(
				es.url,
				es.hasOwnProperty('query') ? es.query : '',
				data_ready_cb
			);
		} else if( es.query_data_cb ) {
			if( !es.hasOwnProperty('cache_key') ) es.cache_key = opts.name+'--'+opts.id.toString();
			_mj_query_entity_list_from_load_cb(
				es.cache_key,
				es.query_data_cb,
				data_ready_cb
			);
		} else if( es.data ) {
			if( !es.hasOwnProperty('cache_key') ) es.cache_key = opts.name+'--'+opts.id.toString();
			_mj_query_entity_list_from_load_cb(
				es.cache_key,
				function _on_query_data( query_success_cb ) {
					query_success_cb(es.data);
				},
				data_ready_cb
			);
		} else {
			throw new Error('no entity source available');
		}
	}

	function _mj_wdg_select_entity_in_overlay( elem, entities, exclude_items, opts, select_cb, select_opts ) {
		mjWidgetOverlayEditor.open(
			elem,
			null,
			function _on_setup( elem, val, ovl ) {
				var wdg_elem = $( document.createElement('span') ).attr({'class':'elem1'}).appendTo(ovl);
				_mj_wdg_create_entity_select( wdg_elem, 'value', null, entities, val, exclude_items, opts.hide_exclusions, opts.show_ids, null, select_opts );
			},
			function _on_extract( ovl ) {
				return $('.elem1',ovl).mjWidget('get');
			},
			function _on_accept( val ) {
				if( select_cb ) select_cb(val);
			},
			null,
			{
				'OverlayCssWidth':'auto',
				'OkButtonText':mjt('add2','!'),
				//, 'WrapOverlayContents':false
			}
		);
	}

	function _mj_init_wdg_entity_select( self, opts, select_opts ) {
		var elem = $(document.createElement('span')).attr({'class':'elem1'})
			.append( $('<select disabled><option>'+mjt('loading')+'</option></select>',{id:opts.id,name:opts.name}) ) // placeholder until entity list loaded
			.appendTo(self);
		_mj_query_entity_list_by_opts(
			opts,
			function _on_data_ready( data ) {
				if( opts.preProcessEntityData ) opts.preProcessEntityData(data);
				if( opts.setup_cb && !select_opts.setup_cb ) select_opts.setup_cb = opts.setup_cb;
				_mj_wdg_create_entity_select( elem, opts.name, opts.id, data.entities, opts.value||'', [], opts.hide_exclusions, opts.show_ids, null, select_opts );
				_mj_wdg_check_add_change( self[0], opts, $('select',self), true );
			}
		);
	}


	function _mj_init_wdg_entity_list( self, opts, items, select_opts ) {
		$(self).data('mj-widget-value',items);
		_mj_query_entity_list_by_opts(
			opts,
			function _on_data_ready( data ) {
				if( opts.preProcessEntityData ) opts.preProcessEntityData(data);

				var i, entity, eid;
				var il = items.length;
				var eal = data.entities.length;
				$(self).empty();

				if( !data.hasOwnProperty('titles_by_key') ) {
					// create hash map from entity id to title
					data.titles_by_key = {};
					for( i=0; i<eal; ++i ) {
						entity = data.entities[i];
						data.titles_by_key[entity.id] = entity.title;
					}
				}

				// build DOM node list of currently selected items
				for( i=0; i<il; ++i ) {
					eid = items[i];
					$('<div class="zcol" mj_item_idx="'+i+'">'
						+'<label class="zlbl"></label>'
						+' <span class="delete-link" tabindex="0">['+mjt('remove')+']</span>'
						+'</div>')
						.children('label')
						.attr('title','ID '+eid)
						.text( data.titles_by_key[eid] || ('(ID '+eid+')') )
						.end()
						.children('span.delete-link')
						.on('click.mjWidget',function() {
							var idx = parseInt( $(this).closest('div.zcol').attr('mj_item_idx') );
							items.splice(idx,1);
							_mj_init_wdg_entity_list(self,opts,items,select_opts);
							return false;
						} )
						.end()
						.appendTo(self);
				}

				// determine number of selectable entities (remaining)
				var selectable_count = 0;
				for( i=0; i<eal; ++i ) {
					if( arr_inArray(data.entities[i].id,items)===-1 ) selectable_count ++;
				}
				if( selectable_count > 0 ) {
					// build DOM for [add entity] action
					$('<div class="zcol"><span class="emph-link">['+mjt('add')+']</span></div>')
						.appendTo(self)
						.find('.emph-link')
						.on('click.mjWidget',function() {
							_mj_wdg_select_entity_in_overlay(
								$(this),
								data.entities,
								items,
								opts,
								function _on_selected( id ) {
									items.push( parseInt(id,10) );
									_mj_init_wdg_entity_list(self,opts,items,select_opts);
								},
								select_opts
							);
						} );
				}
			}
		);
	}


	var widgets = {
		'string': {
			init: function( opts ) {
				var elem = $( '<input type="text" />' ).attr( { id:opts.id, name:opts.name, 'class':'text '+opts.addClass } ).appendTo(this);
				if( opts.hasOwnProperty('placeholder') ) elem.prop('placeholder',opts.placeholder);
				if( opts.hasOwnProperty('restrictions') ) {
					var opr = opts.restrictions, oprk = ['maxlength','minlength','pattern','inputmode'];
					for( var i=0; i<oprk.length; ++i ) {
						if( opr.hasOwnProperty(oprk[i]) ) elem.prop(oprk[i],opr[oprk[i]]);
					}
				}
				_mj_wdg_check_add_change( this, opts, elem );
			},
			get: function( opts ) {
				return $('input[type=text]',this).val();
			},
		},
		'text': {
			init: function( opts ) {
				var elem = $( document.createElement('textarea') ).attr( { id:opts.id, name:opts.name, 'class':'text '+opts.addClass } ).appendTo(this);
				if( opts.hasOwnProperty('placeholder') ) elem.prop('placeholder',opts.placeholder);
				_mj_wdg_check_add_change( this, opts, elem );
			},
			get: function( opts ) {
				return $('textarea',this).val();
			},
		},
		'list': {
			init: function( opts ) {
				var attrs = { id:opts.id, name:opts.name };
				var s2_opts = {};
				if( opts.multiple ) attrs.multiple = true;
				if( opts.addClass ) attrs['class'] = opts.addClass;
				if( opts.select2_options ) s2_opts = opts.select2_options;
				if( opts.ignore_case ) s2_opts.ignore_case = true;
				var dom_elem = document.createElement('select');
				if( opts.values ) {
					let select_options_html = _mj_wdg_build_select_options(opts.values,opts.html_labels,opts.allow_disabled_values);
					if( select_options_html instanceof DocumentFragment ) {
						dom_elem.appendChild(select_options_html);
					} else {
						dom_elem.innerHTML = select_options_html;
					}
				}
				var elem = $(dom_elem)
					.attr(attrs)
					.appendTo(this);
				if( opts.width ) elem.css('width',opts.width);
				if( opts.multiple ) s2_opts.selectionAdapter = $.fn.select2.amd.require('mjSelect2ContainerCssAdapterMultiFoo');
				if( opts.tree ) {
					s2_opts.resultsAdapter = $.fn.select2.amd.require('mjSelect2TreeResultsAdapter');
					s2_opts.dataAdapter = $.fn.select2.amd.require('mjSelect2TreeDataAdapter');
					s2_opts.selectionAdapter = $.fn.select2.amd.require('mjSelect2TreeSelectionAdapter');
				}
				if( opts.dropdownParent ) s2_opts.dropdownParent = $(opts.dropdownParent);
				convertToSelect2WithDefaultFit(elem,true,s2_opts);
				_mj_wdg_check_add_change( this, opts, elem );
			},
			set: function( opts, value ) {
				if( value===null || value==='' ) {
					if( opts.multiple ) {
						// select no value at all ...
						$('select',this).val([]).trigger('change.select2');
					} else if( Array.isArray(opts.values) && opts.values.length>0 ) {
						// select the first available value, instead of selecting a non-existing null-value (behaviour of such option varies between browsers..)
						$('select',this).val(opts.values[0][0]).trigger('change.select2');
					} else if( typeof opts.values === 'string' ) {
						var firstValue = $('select',this).find('option').not('[disabled]').first().attr('value');
						$('select',this).val(firstValue).trigger('change.select2');
					}
				} else {
					var $select = $('select',this);
					$select.val(value);
					$select.trigger('change.select2');
				}
			},
			get: function( opts ) {
				return $('select',this).val();
			},
			add: function( opts, values, value ) {
				$('select',this).append( _mj_wdg_build_select_options(values,opts.html_labels,opts.allow_disabled_values) );
				if( value!==null && value!=='' ) $('select',this).val(value).trigger('change.select2');
			},
			replace: function( opts, values, value ) {
				$('select',this).empty().append( _mj_wdg_build_select_options(values,opts.html_labels,opts.allow_disabled_values) );
				if( value!==null && value!=='') $('select',this).val(value).trigger('change.select2');
			},
			focus: function() {
				$('select',this).select2('focus');
			},
		},
		'radio_list': {
			init: function( opts ) {
				var self = this;
				opts._widget_focus_id = null; // explicitely disable focus through widget label
				if( opts.values ) {
					var vl = opts.values.length;
					for( var i=0; i<vl; ++i ) {
						var v = opts.values[i],
							id2 = _mj_wdg_id();
						$( '<input type="radio" value="'+htmlspecialchars(v[0],true,true)+'" id="'+id2+'" name="'+htmlspecialchars(opts.name,true,true)+'" /><label for="'+id2+'"> '+htmlspecialchars(v[1])+'</label>' )
							.appendTo(self);
					}
				}
				$(self).addClass(opts.radioGroupClass||'radio-group');
				$('input',self).on('click.mjWidget', function(e) {
					$(this).focus();
					opts.change.call(self);
				} );
			},
			set: function( opts, value ) {
				$('input[value='+value+']',this).prop('checked',true);
			},
			get: function( opts ) {
				return $('input:checked',this).val();
			},
		},
		'interval': {
			init: function( opts ) {
				if( !opts.values ) {
					opts.values = [];
					var d,h,v,t;
					// hours 1..23
					for( h=1; h<24; ++h ) {
						v = ('0'+h).slice(-2)+':00:00';
						t = MDLocale.fmt_interval(v);
						opts.values.push( [v,t] );
					}
					// days 1..90
					for( d=1; d<=90; ++d ) {
						v = d + (d==1?' day':' days');
						t = MDLocale.fmt_interval(v);
						opts.values.push( [v,t] );
					}
				}
				// re-define widget as a list widget
				_define_widget_methods(opts,'list');
				_call_widget_method2(this,opts,'init',[opts]);
			},
		},
		'set': {
			init: function( opts ) {
				var self=this;
				opts._widget_focus_id = null; // explicitely disable focus through widget label
				if( opts.values ) {
					var vl=opts.values.length;
					var doc_frag = document.createDocumentFragment();
					for( var i=0; i<vl; ++i ) {
						var v = opts.values[i];
						var id2 = _mj_wdg_id();

						var cb_attrs = { id:id2, name:opts.name+'[]', value:v[0], 'title':v[2]||v[1], 'class':'checkbox' };
						var label_attrs = { 'for':id2, 'title':v[2]||v[1] };
						if( opts.noTooltipOnLabel ) delete label_attrs.title;

						var itm = $( document.createElement('span') ).attr( { 'class':'cb-item' } );
						$( '<input type="checkbox" />' )
							.attr(cb_attrs)
							.on('click.mjWidget', function() {
								$(this).focus().closest('.cb-item').toggleClass('selected',$(this).prop('checked'));
								opts.change.call(self);
							} )
							.appendTo(itm);
						$( document.createElement('label') )
							.attr(label_attrs)
							.html('&nbsp;'+htmlspecialchars(v[1],false,true))
							.appendTo(itm);
						doc_frag.appendChild(itm[0]);
					}
					self.appendChild(doc_frag);
				}
				$(self).addClass(opts.checkboxGroupClass||'checkbox-group');
			},
			set: function( opts, value ) {
				if( !Array.isArray(value) ) value = [];
				$('input[type=checkbox]',this).val(value).each( function() {
					$(this).closest('.cb-item').toggleClass('selected',$(this).prop('checked'));
				});
			},
			get: function( opts ) {
				var vls = [];
				$('input[type=checkbox]:checked',this).each( function() {
					vls.push( $(this).val() );
				});
				return vls;
			},
		},
		'map': {
			init: function( opts ) {
				var self = $(this), container = $(document.createElement('span')).attr({'class':'map-widget-input-container'}).appendTo(this);
				if( !opts.hasOwnProperty('addClass') ) opts.addClass = '';

				function _add_key_wdg( is_first_row, row ) {
					var has_keys = opts.hasOwnProperty('keys');
					var row_opts = {
						name:    opts.name+'[keys][]',
						'class': (has_keys?'mapkey ':'mapkey text ') + opts.addClass,
					};
					if( is_first_row ) row_opts.id = opts.id;
					var new_elem = $( has_keys ? '<select />' : '<input type="text" />' ).attr(row_opts);
					if( has_keys ) new_elem.html( _mj_wdg_build_select_options(opts.keys) );
					return _mj_wdg_check_add_change(self[0],opts,new_elem.appendTo(row));
				}
				function _add_val_wdg( row ) {
					var has_values = opts.hasOwnProperty('values');
					var row_opts = {
						name:    opts.name+'[values][]',
						'class': (has_values?'mapval ':'mapval text ') + opts.addClass,
					};
					var new_elem = $( has_values ? '<select />' : '<input type="text" />' ).attr(row_opts);
					if( has_values ) new_elem.html( _mj_wdg_build_select_options(opts.values,opts.html_labels,opts.allow_disabled_values) );
					return _mj_wdg_check_add_change(self[0],opts,new_elem.appendTo(row));
				}

				var widget_editor = {
					add_row: function( is_first_row, container ) {
						var row = $('<div class="maprow"></div>').appendTo(container);
						_add_key_wdg(is_first_row,row);
						_add_val_wdg(row);
						// + del action
						$('<button class="small mapdel delete-link" type="button">'+mjt('remove')+'</button>')
							.on('click.mjWidget', function(e) {
								e.preventDefault();
								var row = $(this).closest('.maprow');
								row.remove();
								opts.change.call(self);
							} )
							.appendTo(row);
						return row;
					},
					remove_all: function( container ) {
						container.find('.maprow').remove();
					},
					get_normalized_values: function( values ) {
						if( !Array.isArray(values) ) {
							if( typeof values === 'object' ) values = Object.entries(values);
							else values = x_split(''+values,'\n');
						}
						return Array.prototype.map.call( values, function(e) {
							if( Array.isArray(e) ) return e.length===0 ? null : e;
							if( (e=str_trim(e))==='' ) return null;
							return split_once(e,':');
						} );
					},
				};
				self.data('mj-widget-editor',widget_editor);

				// + add action
				$('<div class="link mapadd map-widget-input-add-data" title="'+mjt('add')+'"></div>')
					.on('click.mjWidget', function(e) {
						e.preventDefault();
						var container = $('.map-widget-input-container',self),
							editor = self.data('mj-widget-editor');
						var is_first_row = container.find('.maprow').length===0;
						var row = editor.add_row(is_first_row,container);
						row.find('.mapkey').select();
						opts.change.call(self);
					} )
					.appendTo(this);
			},
			set: function( opts, values ) {
				var self = $(this);
				var editor = self.data('mj-widget-editor');
				values = editor.get_normalized_values(values);
				var container = $('.map-widget-input-container',this);
				editor.remove_all(container);
				for( var i=0; i<values.length; ++i ) {
					var is_first_row = i===0;
					var row = editor.add_row(is_first_row,container);
					row.find('.mapkey').val(values[i][0]).end()
						.find('.mapval').val(values[i][1])
						.select();
				}
			},
			get: function( opts ) {
				var self = $(this);
				var container = $('.map-widget-input-container',this);
				var values = [];
				container.find('.mapkey').each(function() {
					var ke = $(this),
						ve = ke.nextAll('.mapval');
					values.push( [ ke.val(), ve.val() ] );
				} );
				var editor = self.data('mj-widget-editor');
				return editor.get_normalized_values(values);
			},
			focus: function( opts ) {
				$('.mapval',this).last().focus();
			},
			select: function( opts ) {
				$('.mapval',this).last().select();
			},
		},
		'bool': {
			init: function( opts ) {
				var id2 = _mj_wdg_id(),
					self = this;
				opts._widget_focus_id = null; // explicitely disable focus through widget label
				$( '<input type="radio" value="1" id="'+opts.id+'" name="'+htmlspecialchars(opts.name,true,true)+'" /><label for="'+opts.id+'">'+mjt('yes')+'</label>'
					+ '&nbsp;&nbsp;&nbsp;'
					+ '<input type="radio" value="0" id="'+id2+'" name="'+htmlspecialchars(opts.name,true,true)+'" checked="checked" /><label for="'+id2+'">'+mjt('no')+'</label>' )
					.appendTo(this);
				$(self).addClass(opts.radioGroupClass||'radio-group');
				$('input',this).on('click.mjWidget', function(e) {
					$(this).focus();
					opts.change.call(self);
				} );
			},
			set: function( opts, value ) {
				$('input[value='+(value?'1':'0')+']',this).prop('checked',true);
			},
			get: function( opts ) {
				return !!$('input[value=1]',this).prop('checked');
			},
		},
		'bool2': {
			init: function( opts ) {
				var self = this;
				$( '<input type="checkbox" value="1" id="'+opts.id+'" name="'+htmlspecialchars(opts.name,true,true)+'" />' )
					.appendTo(this);
				if( opts.checkboxGroupClass ) $(self).addClass(opts.checkboxGroupClass);
				$('input',this).on('click.mjWidget', function(e) {
					$(this).focus();
					opts.change.call(self);
				} );
			},
			set: function( opts, value ) {
				$('input',this).prop('checked',!!value);
			},
			get: function( opts ) {
				return !!$('input',this).prop('checked');
			},
		},
		'bool3': {
			init: function( opts ) {
				var id2 = _mj_wdg_id(),
					id3 = _mj_wdg_id(),
					self = this;
				opts._widget_focus_id = null; // explicitely disable focus through widget label
				$(  '<input type="radio" value="" id="'+opts.id+'" name="'+htmlspecialchars(opts.name,true,true)+'" checked="checked" /><label for="'+opts.id+'">'+mjt('wdg-bool3-undefined')+'</label>'
					+ '&nbsp;&nbsp;&nbsp;'
					+ '<input type="radio" value="1" id="'+id2+'" name="'+htmlspecialchars(opts.name,true,true)+'" /><label for="'+id2+'">'+mjt('wdg-bool3-yes')+'</label>'
					+ '&nbsp;&nbsp;&nbsp;'
					+ '<input type="radio" value="0" id="'+id3+'" name="'+htmlspecialchars(opts.name,true,true)+'" /><label for="'+id3+'">'+mjt('wdg-bool3-no')+'</label>' )
					.appendTo(this);
				$(self).addClass(opts.radioGroupClass||'radio-group');
				$('input',this).on('click.mjWidget', function(e) {
					$(this).focus();
					opts.change.call(self);
				} );
			},
			set: function( opts, value ) {
				if( value===null || value==='' ) $('input',this).first().prop('checked',true);
				else $('input[value='+(value?'1':'0')+']',this).prop('checked',true);
			},
			get: function( opts ) {
				if( $('input[value=1]',this).prop('checked') ) return true;	// yes
				if( $('input[value=0]',this).prop('checked') ) return false;	// no
				return null;													// undefined
			},
		},
		'download_formats': {
			init: function( opts ) {
				var self = $(this);
				var mc_do_len = mj.mc_display_order.length;
				self.data('loading',true).hide();
				// build list of select elements
				for( var idx=0; idx<mc_do_len; ++idx ) {
					var mc_id = mj.mc_display_order[idx],
						mc_title = mj.mc_names[mc_id];
					var id2 = _mj_wdg_id();
					if( idx===0 ) opts._widget_focus_id = id2;
					$( '<div class="zcol">'
						+ '<label class="zlbl" for="'+id2+'"></label>'
						+ '<span id="zitem-'+id2+'" class="zitem"><select/></span>'
						+'</div>')
						.children('label')
						.text(mc_title)
						.end()
						.children('span').children('select')
						.attr('id',id2)
						.attr('name',opts.name+'['+mc_id+']')
						.end().end()
						.appendTo(this);
				}
				// load list of available download formats
				$.postJSON( 'json/widgets/download_formats-list', '', function(j) {
					if( j && j.download_formats ) {
						var j_download_formats_len = j.download_formats.length;
						for( var idx=0; idx<j_download_formats_len; ++idx ) {
							var dlf = j.download_formats[idx],
								mc = dlf.media_class,
								trg_select = self.find('select[name="'+opts.name+'['+mc+']"]');
							$('<option/>',{value:dlf.id})
								.text(dlf.name)
								.appendTo(trg_select);
						}
					}
					self.show().removeData('loading');
					var delayed_set = self.data('delayed-set');
					if( delayed_set ) {
						_call_widget_method2(self[0],opts,'set',[opts,delayed_set]);
						self.removeData('delayed-set');
					}
				} );
				$('select',this).select2().change( function(e) {
					opts.change.call(self);
				} );
			},
			set: function( opts, value ) {
				if( typeof value == 'object' && value!==null ) {
					if( $(this).data('loading') ) {
						// store values until the widget is loaded completely
						$(this).data('delayed-set',value);
					} else {
						// apply values to select elements
						$('select',this).each(function() {
							var name = $(this).attr('name'),
								brp = name.lastIndexOf('[');
							if( brp != -1 ) {
								var mc = name.substring(brp+1,name.length-1);
								if( mc in value ) $(this).val( value[mc] );
							}
						});
					}
				}
			},
			get: function( opts ) {
				throw new Error('mjWidget: get method not yet implemented for type=download_formats');
			},
		},
		'date': {
			init: function( opts ) {
				var id1 = _mj_wdg_id(),
					id2 = opts.id,
					id3 = _mj_wdg_id(),
					h = $( '<input type="hidden" />' ).attr( { id:id1, name:opts.name } ).appendTo(this), // hidden input (sql date: YYYY-MM-DD)
					e = $( '<input type="text" />' ).attr( { id:id2, 'class':'text date '+opts.addClass, autocomplete:'off' } ).appendTo(this), // visible input (formatted date)
					r = $( document.createElement('span') ).attr( { id:id3, 'class':'reset' } ).appendTo(this), // reset button
					self = this;
				// setup datepicker
				var dp_opts = {
					altField:  '#'+id1,
					altFormat: 'yy-mm-dd',
					onSelect:  function() {
						r.css('visibility','visible');
						opts.change.call(self);
					},
				};
				if( opts.hasOwnProperty('minDate') ) dp_opts.minDate = opts.minDate;
				if( opts.hasOwnProperty('yearRange') ) dp_opts.yearRange = opts.yearRange;
				e.datepicker(dp_opts).change( function(e) {
					var disp_v = $(this).val(),
						intr_v = MDLocale.xtr_date(disp_v);
					if( intr_v=='' ) $(this).val('');
					h.val(intr_v);
					r.css('visibility',intr_v==''?'hidden':'visible');
					opts.change.call(self);
				} );
				// setup reset button
				r.on('click.mjWidget', function() {
					h.val('');
					e.val('');
					r.css( 'visibility', 'hidden' );
					opts.change.call(self);
				} );
			},
			set: function( opts, value ) {
				$('input[type=hidden]',this).val(value);
				$('input[type=text]',this).val(MDLocale.fmt_date(value));
				$('span',this).css('visibility',value==''?'hidden':'visible');
			},
			get: function( opts ) {
				return $('input[type=hidden]',this).val();
			},
		},
		'datetime': {
			init: function( opts ) {
				var id1 = _mj_wdg_id(),
					id2 = opts.id,
					id3 = _mj_wdg_id(),
					id4 = _mj_wdg_id(),
					self = $(this),
					h, e, t, r;
				h = $( '<input type="hidden" />' ).attr( { id:id1, name:opts.name } ).appendTo(this); // hidden input (sql date: YYYY-MM-DD hh:mm:ss)
				e = $( '<input type="text" />' ).attr( { id:id2, 'class':'text date '+opts.addClass, autocomplete:'off' } ).appendTo(this); // visible input (formatted date)
				self.append(' ');
				t = $( '<input type="text" />' ).attr( { id:id3, 'class':'text time '+opts.addClass, autocomplete:'off' } ).appendTo(this); // visible input (formatted time)
				r = $( document.createElement('span') ).attr( { id:id4, 'class':'reset-time' } ).appendTo(this); // reset-time button
				var _nrm_vtime = function() {
					var vtime=str_trim(t.val());
					if( vtime.length ) {
						// normalize time
						var p = vtime.split(':');
						if( p.length>0 ) {
							// hours
							p[0] = parseInt(p[0],10);
							p[0] = p[0]<0 ? 0 : ( p[0]>23 ? 23 : p[0] );
							if( p.length>1 ) {
								// minutes
								p[1] = parseInt(p[1],10);
								p[1] = p[1]<0 ? 0 : ( p[1]>59 ? 59 : p[1] );
								if( p.length>2 ) {
									p[2] = parseInt(p[2],10);
									p[2] = p[2]<0 ? 0 : ( p[2]>59 ? 59 : p[2] );
								}
							}
							vtime = p.length>2 ? sprintf('%02d:%02d:%02d',p[0]||0,p[1]||0,p[2]||0) : sprintf('%02d:%02d',p[0]||0,p[1]||0);
							t.val(vtime);
						}
					}
				};
				var _upd_hidden = function() {
					var vdate=str_trim(e.val()),
						vtime=str_trim(t.val());
					// date only
					if( vtime=='' ) h.val(MDLocale.xtr_date(vdate));
					// date+time
					h.val(MDLocale.xtr_datetime(vdate,vtime));
					// trigger change event
					opts.change.call(self);
				};
				// setup datepicker
				var dp_opts = {
					onSelect:function() {
						_upd_hidden();
					},
				};
				if( opts.hasOwnProperty('minDate') ) dp_opts.minDate = opts.minDate;
				if( opts.hasOwnProperty('yearRange') ) dp_opts.yearRange = opts.yearRange;
				e.datepicker(dp_opts).on('change.mjWidget keyup.mjWidget onpaste.mjWidget',_upd_hidden);
				// setup time input field
				t.on('change.mjWidget',_nrm_vtime).on('change.mjWidget keyup.mjWidget onpaste.mjWidget',_upd_hidden);
				// setup reset button
				r.on('click.mjWidget', function() {
					if( t.val()==='' ) e.val('');
					t.val('');
					_upd_hidden();
				} );
				r.on('dblclick.mjWidget', function() {
					e.val('');
					t.val('');
					_upd_hidden();
				} );
			},
			set: function( opts, value ) {
				$('input[type=hidden]',this).val(value);
				var vdate, vtime;
				vdate = MDLocale.fmt_date(value);
				if( value.length==10 ) {
					vtime = '';
				} else {
					vtime = MDLocale.fmt_time(value);
				}
				$('input.date',this).val(vdate);
				$('input.time',this).val(vtime);
			},
			get: function( opts ) {
				return $('input[type=hidden]',this).val();
			},
		},

		'ml-string': {
			init: function( opts ) {
				var self = $(this),
					elem = $( '<input type="text" />' ).attr( { id:opts.id, name:opts.name, 'class':'text '+opts.addClass } ).appendTo(this);

				function open_ml_editor() {
					MVTranslationsEditor.open2( elem, elem.val(), 'text/plain-string',
						// accept_cb
						function(nv) {
							elem.val(nv);
							opts.change.call(self[0]);
							elem.data('mj-ml-editor-closing',true);
							elem.select();
							elem.removeData('mj-ml-editor-closing');
						},
						// close_cb
						function() {
							elem.data('mj-ml-editor-closing',true);
							elem.select();
							elem.removeData('mj-ml-editor-closing');
						},
						opts
					);
				}
				$(document.createElement('span')).attr({'class':'ml-string-widget-button'})
					.on('click.mjWidget', function(e) {
						e.preventDefault();
						e.stopPropagation();
						open_ml_editor();
					})
					.appendTo(this);
				elem.keydown( function(e) {
					if( e.which==40 && !( e.altKey || e.ctrlKey || e.shiftKey ) ) {
						e.preventDefault();
						e.stopPropagation();
						open_ml_editor();
						return false;
					}
				} );
				_mj_wdg_check_add_change( this, opts, elem );
				elem.focus( function(e) {
					if( elem.data('mj-ml-editor-closing') ) {
						// ignore further handlers ...
						return true;
					}
					e.preventDefault();
					e.stopPropagation();
					var v = elem.val();
					if( v.length>0 && v[0]==='{' ) {
						// value is multi-lingu -> automatically open translations editor
						open_ml_editor();
					} else {
						// value is not multi-lingu -> work with plain text input (but user may explicitely call multi-lingu editor)
						MVTranslationsEditor.close();
					}
				});
			},
			set: function( opts, value ) {
				MVTranslationsEditor.close();
				$('input[type=text]',this).val(value);
			},
			get: function( opts ) {
				return $('input[type=text]',this).val();
			},
		},

		'ml-text': {
			init: function( opts ) {
				var self = $(this),
					elem = $(document.createElement('textarea')).attr( { id:opts.id, name:opts.name, 'class':'text '+opts.addClass } ).appendTo(this),
					btn_container = $(document.createElement('span')).attr({'class':'ml-text-widget-button-container'}).appendTo(this);

				function open_ml_editor() {
					elem.mjCodeEditorWidget('convertToTextArea');
					MVTranslationsEditor.open2( elem, elem.val(), opts.ctype,
						// accept_cb
						function(nv) {
							elem.val(nv);
							elem.mjCodeEditorWidget('setValue',nv);
							opts.change.call(self[0]);
						},
						// close_cb
						function() {
							elem.mjCodeEditorWidget('convertToEditor');
						},
						opts
					);
				}

				$(document.createElement('span')).attr({'class':'ml-text-widget-button'})
					.on('click.mjWidget', function(e) {
						e.preventDefault();
						e.stopPropagation();
						open_ml_editor();
					}).appendTo(btn_container);
				if( opts.css ) elem.css(opts.css);

				function handle_focus(e) {
					var v = elem.val();
					if( v.length>0 && v[0]==='{' ) {
						// value is multi-lingu -> automatically open translations editor
						e.preventDefault();
						e.stopPropagation();
						open_ml_editor();
					} else {
						// value is not multi-lingu -> work with plain text input (but user may explicitely call multi-lingu editor)
						MVTranslationsEditor.close();
					}
				}

				// add focus event handler to the plain textarea...
				elem.focus( function(e) {
					handle_focus(e);
				});

				// setup code mirror instance
				var emode = opts.ctype==='text/html' ? 'x-mj-i18n-html' : 'x-mj-i18n-text';
				elem.mjCodeEditorWidget( {
					mode: emode,
					css: opts.css ? opts.css : null,
					editor: function( evt, ui ) {
						var cm = elem.mjCodeEditorWidget('getCodeMirrorInstance');
						// add focus event handler on code mirror instance
						cm.on('focus.mjWidget',function(cm,e) {
							handle_focus(e);
						});
						cm.on('gutterClick.mjWidget',function(cm,l,g,e) {
							handle_focus(e);
						});
						cm.on('mousedown.mjWidget',function(cm,e) {
							handle_focus(e);
						});
					},
					textarea: function( evt, ui ) {
					},
				} );
				_mj_wdg_check_add_change( this, opts, elem );

			},
			set: function( opts, value ) {
				var ta = $('#'+opts.id);
				MVTranslationsEditor.close();
				ta.val(value);
				ta.mjCodeEditorWidget('setValue',value);
			},
			get: function( opts ) {
				var ta = $('#'+opts.id);
				return ta.mjCodeEditorWidget('getValue');
			},
		},
		'ml-string-2': {
			init: function( opts ) {
				var id1 = _mj_wdg_id();

				var self = $(this),
					container = $(document.createElement('div')).appendTo(this),
					hidden_elem = $( '<input type="hidden" />' ).attr( { id:id1, name:opts.name, 'class':'text '+opts.addClass } ).appendTo(container);

				var locales = opts.hasOwnProperty('locales') && opts.locales!==null ? opts.locales : mj.ui_locales,
					default_locale = opts.hasOwnProperty('default_locale') && opts.default_locale!==null ? opts.default_locale : mj.ui_locales[0];

				function _update_ml_value() {
					var value = {};
					for( var i=0; i<locales.length; ++i ) {
						var lk = locales[i];
						value[lk] = $('input.ml-ltext[name='+lk+']',container).val();
					}
					value = JSON.stringify(value);
					hidden_elem.val(value);
					return value;
				}

				function _build_ml_object( str, locales ) {
					var obj = {};
					for( var i=0; i<locales.length; ++i ) obj[locales[i]] = str;
					return obj;
				}

				function get_ml_value_object( val ) {
					var val_type = typeof val;
					if( val_type==='string' ) {
						if( val.length>0 && val[0]==='{' ) {
							// from json string
							try {
								val = JSON.parse(val);
							} catch( err ) {
								// use plain string
								val = _build_ml_object(val,locales);
							}
						} else {
							// from simple string
							val = _build_ml_object(val,locales);
						}
					} else if( val_type==='object' ) {
						// from object (no conversion required)
					}
					if( val===null ) val = _build_ml_object('',locales);
					return val;
				}

				function setup_widgets( val ) {
					val = get_ml_value_object(val);
					// add translation value inputs
					container.children('.ml-item').remove();
					for( var index=0; index<locales.length; ++index ) {
						var lk = locales[index];
						var ltag_title = lk;
						var ml_item = $('<div class="ml-item"><span class="ml-ltag">'+htmlspecialchars(ltag_title)+'</span></div>').appendTo(container);
						var iv = val.hasOwnProperty(lk) ? val[lk] : ( val.hasOwnProperty(default_locale) ? val[default_locale] : '' );
						var id2;
						if( index===0 ) id2 = opts.id;
						else id2 = _mj_wdg_id();
						var input_elem = $('<input type="text" />').attr( { id:id2, name:lk, 'class':'text ml-ltext' } ).val(iv).appendTo(ml_item);
						var input_elem_opts = {
							internal_prompt_change: function( $initiating_elem ) {
								_update_ml_value();
								if( opts.with_reset_icon ) {
									let $ml_item_elem = $initiating_elem.closest('.ml-item');
									let reset_elem = $ml_item_elem.find('span.reset');
									let input_elem = $ml_item_elem.find('input');
									const reset_locale = input_elem.attr('name');
									const reset_value = opts.get_reset_value ? opts.get_reset_value.call(self,reset_locale) : '';
									reset_elem.toggle( input_elem.val()!==reset_value );
								}
							},
						};
						if( opts.prompt_change ) input_elem_opts.prompt_change = opts.prompt_change;
						if( opts.change ) input_elem_opts.change = opts.change;
						input_elem = _mj_wdg_check_add_change(this,input_elem_opts,input_elem);
						// console.log({w:'setup_widgets',opts});
						if( opts.with_reset_icon ) {
							var reset_elem = $('<span />').attr( { 'class':'reset' } ).css({visibility:'visible'}).appendTo(ml_item);
							if( opts.reset_icon_title ) reset_elem.attr('title',opts.reset_icon_title);
							reset_elem.click( function() {
								let reset_elem = $(this);
								let input_elem = reset_elem.closest('.ml-item').find('input');
								const reset_locale = input_elem.attr('name');
								const reset_value = opts.get_reset_value ? opts.get_reset_value.call(self,reset_locale) : '';
								// console.log({w:'RESET',reset_locale,reset_value,opts});
								// console.log({w:'RESET rv1',reset_value,opts});
								input_elem.val(reset_value).select().focus();
								reset_elem.hide();
								_update_ml_value();
								if( opts.internal_prompt_change ) opts.internal_prompt_change.call(self);
								if( opts.prompt_change ) opts.prompt_change.call(self);
								else if( opts.change ) opts.change.call(self);
							} );
							// initial visibility of reset_elem
							const reset_locale = input_elem.attr('name');
							const reset_value = opts.get_reset_value ? opts.get_reset_value.call(self,reset_locale) : '';
							reset_elem.toggle( input_elem.val()!==reset_value );
						}
					}
					container.find('.ml-ltag').addClass('link').on('click.mjWidget', function() {
						$(this).next('input[type=text]').select();
					});
				}
				self.data('mj-widget-editor',{
					get_ml_value_object,
					setup_widgets
				} );
				setup_widgets( {} );
				_mj_wdg_check_add_change( this, opts, hidden_elem );
				_update_ml_value();
			},
			set: function( opts, value ) {
				const editor = $(this).data('mj-widget-editor');
				value = editor.get_ml_value_object(value);
				$('div > input[type=hidden]',this).val( JSON.stringify(value) );
				editor.setup_widgets(value);
				// let taValue = ( value.getValue ? value.getValue(value) : value.value );
				// console.log({w:'taValue',taValue});
				// let gvval = taValue;
				// // if( gvval )
				// // return getValue;
				// // return ta.mjCodeEditorWidget('getValue');
			},
			get: function( opts ) {
				const editor = $(this).data('mj-widget-editor');
				let _update_ml_value = function( x ) {
					return x;
				};
				// let xxx = _update_ml_value();
				var hidden_elem = $('div > input[type=hidden]',this);
				var value = hidden_elem.val();
				// console.log({w:'ml-string-2',ed:editor[0]});
				// console.log({w:'ml-string-2',value});
				return value;
			},
		},

		'string-list': {
			init: function( opts ) {
				var self = $(this), container = $(document.createElement('span')).attr({'class':'string-list-widget-input-container'}).appendTo(this);
				if( !opts.hasOwnProperty('addClass') ) opts.addClass = '';

				function add_item( is_first_item, item_value ) {
					var container = $('.string-list-widget-input-container',self),
						attrs = {name:opts.name+'[]','class':'text '+opts.addClass},
						elem;
					if( is_first_item ) attrs.id = opts.id;
					elem = _mj_wdg_check_add_change(self[0],opts,$('<input type="text" />').attr(attrs).appendTo(container));
					if( item_value!==null ) elem.val(item_value);
					if( !is_first_item ) elem.select();

					elem.on('keydown.mjWidget',function(e) {
						if( e.ctrlKey || e.metaKey || e.shiftKey ) return; // ignore
						var k = e.keyCode,
							vl = elem.val().length;
						if( k>=37 && k<=40 ) {
							if( $.fn.getSelectionStart && $.fn.getSelectionEnd ) {
								var ss = $.fn.getSelectionStart(elem[0]),
									se = $.fn.getSelectionEnd(elem[0]);
								if( ss===se ) {
									// nothing selected, just check caret position...
									if( (k===37||k===38) && ss!==0 ) return;
									else if( (k===39||k===40) && ss!==vl ) return;
								} else if( ss!==se && !(ss===0&&se===vl) ) {
									// something but not all selected
									return;
								} else if( ss===0 && se===vl && (k===37||k===39) ) {
									// all selected, but arrow left or right
									return;
								}
							}
						}
						if( k===37 || k===38 ) {			// ARROW LEFT or UP
							var prev_elem = elem.prevAll('input[type=text]').first();
							if( prev_elem.length ) {
								prev_elem.select();
								e.preventDefault();
								return false;
							}
						} else if( k===39 || k===40 ) {		// ARROW RIGHT or DOWN
							var next_elem = elem.nextAll('input[type=text]').first();
							if( next_elem.length ) {
								next_elem.select();
								e.preventDefault();
								return false;
							} else if( vl!==0 ) {
								add_item(false,null);
								e.preventDefault();
								return false;
							}
						}
					} );

					return elem;
				}

				// add first input
				add_item(true,null);

				// + "add" action
				$('<div class="link string-list-widget-input-add-data" title="'+mjt('add')+'"></div>')
					.on('click.mjWidget', function(e) {
						e.preventDefault();
						e.stopPropagation();
						add_item(false,null);
					} )
					.appendTo(this);
				self.data('mj-widget-editor',add_item);

				//TODO: + "del" action
			},
			set: function( opts, v ) {
				var container = $('.string-list-widget-input-container',this),
					first_input = $('input[type=text]',container).first();
				first_input.siblings().remove();

				var add_item = $(this).data('mj-widget-editor');

				if( Array.isArray(v) ) {
					if( opts.hasOwnProperty('remove_whitespace_and_empty_items') && !!opts.remove_whitespace_and_empty_items ) {
						v = v.map( function(e) {
							e = str_trim(e);
							return e!=='' ? e : null;
						} );
					}
					first_input.val(v.length>0?v[0]:'');
					for( var i=1; i<v.length; ++i ) {
						add_item(false,v[i]);
					}
				} else {
					first_input.val(v);
				}
			},
			get: function( opts ) {
				var v = [];
				$('input[type=text]',this).each(function() {
					v.push( $(this).val() );
				} );
				if( opts.hasOwnProperty('remove_whitespace_and_empty_items') && !!opts.remove_whitespace_and_empty_items ) {
					v = v.map( function(e) {
						e = str_trim(e);
						return e!=='' ? e : null;
					} );
				}
				return v;
			},
			focus: function( opts ) {
				$('input[type=text]',this).last().focus();
			},
			select: function( opts ) {
				$('input[type=text]',this).last().select();
			},
		},

		'int': {
			init: function( opts ) {
				var unit_label = opts.unit_label || '',
					elem = $( '<input type="text" />' ).attr( { id:opts.id, name:opts.name, 'class':'text number int '+opts.addClass } ).appendTo(this);

				elem.numeric( { decimal:false, negative:!(opts.hasOwnProperty('no_negative_values')&&opts.no_negative_values) } );

				if( unit_label ) $(document.createElement('label')).attr( {'for':opts.id,'class':'unit '+opts.addClass} ).text(unit_label).appendTo(this);
				_mj_wdg_check_add_change( this, opts, elem );
			},
			set: function( opts, v ) {
				$('input[type=text]',this).val(MDLocale.fmt_number_input(v));
			},
			get: function( opts ) {
				var v = $('input[type=text]',this).val();
				if( v==='' ) return ''; // = empty value, but not 0
				v = parseInt(MDLocale.xtr_number_input(v),10);
				if( isNaN(v) ) return 0;
				return v;
			},
		},

		'float': {
			init: function( opts ) {
				var unit_label = opts.unit_label || '',
					elem = $( '<input type="text" />' ).attr( { id:opts.id, name:opts.name, 'class':'text number float '+opts.addClass } ).appendTo(this);

				elem.numeric( { decimal:MDLocale.decimal_point_char, negative:!(opts.hasOwnProperty('no_negative_values')&&opts.no_negative_values) } );

				if( unit_label ) $(document.createElement('label')).attr( {'for':opts.id,'class':'unit '+opts.addClass} ).text(unit_label).appendTo(this);
				_mj_wdg_check_add_change( this, opts, elem );
			},
			set: function( opts, v ) {
				$('input[type=text]',this).val(MDLocale.fmt_number_input(v));
			},
			get: function( opts ) {
				var v = $('input[type=text]',this).val();
				if( v==='' ) return ''; // = empty value, but not 0
				v = parseFloat(MDLocale.xtr_number_input(v));
				if( isNaN(v) ) return 0;
				return v;
			},
		},

		'special-float': {
			init: function( opts ) {
				var unit_label = opts.unit_label || '',
					accepted_prefixes = opts.hasOwnProperty('accepted_prefixes') ? opts.accepted_prefixes : '',
					elem = $( '<input type="text" />' ).attr( { id:opts.id, name:opts.name, 'class':'text number float '+opts.addClass } ).appendTo(this);
				elem.numeric( { decimal:MDLocale.decimal_point_char, negative:!(opts.hasOwnProperty('no_negative_values')&&opts.no_negative_values), acceptedPrefixes:accepted_prefixes } );
				if( unit_label ) $(document.createElement('label')).attr( {'for':opts.id,'class':'unit'+(opts.hasOwnProperty('unit_label_addClass')?' '+opts.unit_label_addClass:'')} ).text(unit_label).appendTo(this);
				_mj_wdg_check_add_change( this, opts, elem );
			},
			set: function( opts, v ) {
				var lsv = MDLocale.fmt_number_input(v);
				if( opts.hasOwnProperty('preserve_significance') && opts.preserve_significance ) {
					// nothing special required here
				}
				$('input[type=text]',this).val(lsv);
			},
			get: function( opts ) {
				var v = str_trim($('input[type=text]',this).val()),
					l = v.length,
					vi, p='', ulsv,
					o, x, f;
				if( l===0 ) return '';
				if( opts.hasOwnProperty('accepted_prefixes') && opts.accepted_prefixes.length>0 ) {
					for( vi=0; vi<l; ++vi ) {
						if( (x=opts.accepted_prefixes.indexOf(v[vi])) >= 0 ) p += opts.accepted_prefixes.charAt(x);
						else break;
					}
					ulsv = MDLocale.xtr_number_input(v.substr(vi));
				} else {
					ulsv = MDLocale.xtr_number_input(v);
				}
				if( ulsv==='' ) return ''; // = empty value, but not 0
				if( opts.hasOwnProperty('preserve_significance') && opts.preserve_significance ) {
					return p+ulsv;
				}
				f = parseFloat(ulsv);
				if( isNaN(f) ) f = '0';
				return p+f;

			},
		},

		'int-vector': {
			init: function( opts ) {
				var nelements = opts.nelements || 3,
					labels = opts.labels || [],
					unit_labels = opts.unit_labels || [];
				for( var i=0; i<nelements; ++i ) {
					var id2 = _mj_wdg_id();
					if( i===0 ) opts._widget_focus_id = id2;

					if( labels[i] ) $(document.createElement('label')).attr( {'for':id2,'class':'pfx '+opts.addClass} ).text(labels[i]).appendTo(this);
					var elem = $('<input type="text" />').attr( { id:id2, name:opts.name+'['+i+']', 'class':'text number int '+opts.addClass } ).appendTo(this);

					elem.numeric( { decimal:false, negative:!(opts.hasOwnProperty('no_negative_values')&&opts.no_negative_values) } );

					if( unit_labels[i] ) $(document.createElement('label')).attr( {'for':id2,'class':'unit '+opts.addClass} ).text(unit_labels[i]).appendTo(this);
					_mj_wdg_check_add_change( this, opts, elem );
				}
			},
			set: function( opts, v ) {
				var nelements = opts.nelements || 3,
					convert_from_to_string = opts.hasOwnProperty('convert_from_to_string') && !!opts.convert_from_to_string;
				if( !Array.isArray(v) ) {
					if( convert_from_to_string && v=='' ) {
						v = [];
						for( var i=0; i<nelements-1; ++i ) v.push('');
					} else {
						v = v.split(',');
					}
				}
				$('input[type=text]',this).each( function( idx ) {
					$(this).val( (typeof v[idx] === 'undefined') ? '' : MDLocale.fmt_number_input(v[idx]) );
				} );
			},
			get: function( opts ) {
				var convert_from_to_string = opts.hasOwnProperty('convert_from_to_string') && !!opts.convert_from_to_string,
					r = [],
					all_elems_empty = true;
				$('input[type=text]',this).each( function( idx ) {
					var v = $(this).val();
					if( typeof v === 'undefined' || v==='' ) {
						r.push(''); // = empty value, but not 0
					} else {
						all_elems_empty = false;
						v = parseInt(MDLocale.xtr_number_input(v),10);
						if( isNaN(v) ) v = 0;
						r.push(v);
					}
				} );
				if( convert_from_to_string ) return all_elems_empty ? '' : r.join(',');
				return r;
			},
		},

		'float-vector': {
			init: function( opts ) {
				var nelements = opts.nelements || 3,
					labels = opts.labels || [],
					unit_labels = opts.unit_labels || [];
				for( var i=0; i<nelements; ++i ) {
					var id2 = _mj_wdg_id();
					if( i===0 ) opts._widget_focus_id = id2;

					if( labels[i] ) $(document.createElement('label')).attr( {'for':id2,'class':'pfx '+opts.addClass} ).text(labels[i]).appendTo(this);
					var elem = $('<input type="text" />').attr( { id:id2, name:opts.name+'['+i+']', 'class':'text number float '+opts.addClass } ).appendTo(this);

					elem.numeric( { decimal:MDLocale.decimal_point_char, negative:!(opts.hasOwnProperty('no_negative_values')&&opts.no_negative_values) } );

					if( unit_labels[i] ) $(document.createElement('label')).attr( {'for':id2,'class':'unit '+opts.addClass} ).text(unit_labels[i]).appendTo(this);
					_mj_wdg_check_add_change( this, opts, elem );
				}
			},
			set: function( opts, v ) {
				var nelements = opts.nelements || 3,
					convert_from_to_string = opts.hasOwnProperty('convert_from_to_string') && !!opts.convert_from_to_string;
				if( !Array.isArray(v) ) {
					if( convert_from_to_string && v=='' ) {
						v = [];
						for( var i=0; i<nelements-1; ++i ) v.push('');
					} else {
						v = v.split(',');
					}
				}
				$('input[type=text]',this).each( function( idx ) {
					$(this).val( (typeof v[idx] === 'undefined') ? '' : MDLocale.fmt_number_input(v[idx]) );
				} );
			},
			get: function( opts ) {
				var convert_from_to_string = opts.hasOwnProperty('convert_from_to_string') && !!opts.convert_from_to_string,
					r = [],
					all_elems_empty = true;
				$('input[type=text]',this).each( function( idx ) {
					var v = $(this).val();
					if( typeof v === 'undefined' || v==='' ) {
						r.push(''); // = empty value, but not 0
					} else {
						all_elems_empty = false;
						v = parseFloat(MDLocale.xtr_number_input(v),10);
						if( isNaN(v) ) v = 0;
						r.push(v);
					}
				} );
				if( convert_from_to_string ) return all_elems_empty ? '' : r.join(',');
				return r;
			},
		},

		'dyna-set': {
			init: function( opts ) {
				var self = $(this);
				var elem = opts.useThisElement ? self : $(document.createElement('span')).attr( { id:opts.id, 'class':'emph-link '+opts.addClass } ).text( opts.hasOwnProperty('edit_label') ? opts.edit_label : mjt('select')+' …' ).appendTo(this);
				function open_editor(e) {
					var val = _call_widget_method2(self[0],opts,'get',[opts]);
					var setup_cb = function( elem, val, ovl ) {
						var values = [];
						if( opts.hasOwnProperty('get_values') ) values = opts.get_values();
						if( opts.hasOwnProperty('notes1_html') ) $(document.createElement('span')).attr({'class':'mj-dyna-set-widget-overlay-notes1'}).html( opts.notes1_html ).appendTo(ovl);
						$(document.createElement('span')).attr({'class':'mj-dyna-set-widget-overlay-inner'})
							.mjWidget(
								Object.assign(
									{
										type:'set',
										name:'_mjw'+opts.name,
										values:values,
										'default':val,
									},
									opts.setOptions||{}
								) )
							.appendTo(ovl);
						if( opts.hasOwnProperty('notes2_html') ) $(document.createElement('span')).attr({'class':'mj-dyna-set-widget-overlay-notes2'}).html( opts.notes2_html ).appendTo(ovl);
					};
					var extract_cb = function( ovl) {
						var nv = ovl.find('.mj-dyna-set-widget-overlay-inner').mjWidget('get');
						return nv;
					};
					var accept_cb = function(nv) {
						_call_widget_method2(self[0],opts,'set',[opts,nv]);
						opts.change.call(self[0]);
					};
					return mjWidgetOverlayEditor.open(elem,val,setup_cb,extract_cb,accept_cb,null,opts.overlayOptions||null);
				}
				$(this).data('mj-widget-editor',open_editor);
				elem.on('click.mjWidget', function(e) {
					e.preventDefault();
					e.stopPropagation();
					if( mjWidgetOverlayEditor.isOpen(elem) ) mjWidgetOverlayEditor.close();
					else open_editor(e);
				} );
			},
			set: function( opts, value ) {
				var self = $(this),
					i, l;
				mjWidgetOverlayEditor.close();
				if( !Array.isArray(value) ) value = value.split(',');
				$('input[type=hidden]',this).remove();
				for( i=0, l=value.length; i<l; ++i ) {
					$('<input type="hidden" />')
						.attr( { name:opts.name+'[]', value:value[i] } )
						.appendTo(self);
				}
			},
			get: function( opts ) {
				var value = [];
				$('input[type=hidden]',this).each( function() {
					value.push( $(this).val() );
				} );
				return value;
			},
			focus: function( opts ) {
				var open_editor = $(this).data('mj-widget-editor');
				open_editor();
			},
			select: function( opts ) {
				var open_editor = $(this).data('mj-widget-editor');
				open_editor();
			},
		},

		'tree-set': {
			init: function( opts ) {
				var self = this;
				if( opts.values ) {
					var vl = opts.values.length,
						check_selectable_flag = opts.hasOwnProperty('check_selectable_flag') && !!opts.check_selectable_flag;
					for( var i=0; i<vl; ++i ) {
						var v = opts.values[i],
							id2 = _mj_wdg_id(),
							itm = $(document.createElement('span')).attr({'class':'cb-item'}),
							selectable = check_selectable_flag ? !!v[3] : true;
						if( selectable ) {
							$( '<input type="checkbox" />' )
								.attr( { id:id2, name:opts.name+'[]', value:v[0], 'class':'checkbox' } )
								.appendTo(itm)
								.on('click.mjWidget', function() {
									$(this).focus().closest('.cb-item').toggleClass('selected',$(this).prop('checked'));
									opts.change.call(self);
								} )
								.css('marginLeft',(6+22*(v[2]-1))+'px') // (6+22*(depth-1)) px
								.prepend();
						} else {
							itm.addClass('cb-item-unselectable');
						}
						$(document.createElement('label')).attr({'for':id2})
							.text(v[1])
							.attr('title',v[1])
							.prepend('&nbsp;')
							.appendTo(itm);
						itm.appendTo(this);
					}
				}
				$(self).addClass(opts.checkboxGroupClass||'checkbox-group');
			},
			set: function( opts, value ) {
				if( !Array.isArray(value) ) value = [];
				$('input:checkbox',this).val(value).each( function() {
					$(this).closest('.cb-item').toggleClass('selected',$(this).prop('checked'));
				});
			},
			get: function( opts ) {
				var vls = [];
				$('input:checkbox:checked',this).each( function() {
					vls.push( $(this).val() );
				});
				return vls;
			},
		},

		'click2edit-text': {
			init: function( opts ) {
				var self = $(this),
					c2o_elem = $(document.createElement('span'))
						.attr( { id:opts.id, 'class':'mj-click2edit-text-widget-edit-link emph-link '+opts.addClass, 'tabindex':opts.focusable?0:-1 } )
						.text( opts.hasOwnProperty('edit_label') ? opts.edit_label : mjt('select')+' …' )
						.prependTo(this);

				var preview_elem = $(document.createElement('span'))
					.attr({'class':'mj-click2edit-text-widget-inline-preview empty'})
					.attr('tabindex',-1)
					.html('<span class="mj-click2edit-text-widget-inline-preview-1"></span>')// tabindex="-1"></span>')
					.insertBefore(c2o_elem);

				var ev_ns = '.mjWidget-'+opts.id;

				function _inl_prev_blur( do_blur ) {
					preview_elem.removeClass('focused');
					if( do_blur ) preview_elem.blur();
					$(document).off(ev_ns);
				}

				function close_cb(c2o_elem) {
					_inl_prev_blur();
					if( opts.focusable && c2o_elem ) c2o_elem.focus();
					if( opts.close ) opts.close.call(self[0]);
				}

				function _inl_prev_focus( ) {
					preview_elem.addClass('focused');
					// any click outside of widget will close overlay ...
					$(document).on('click'+ev_ns+' mousedown'+ev_ns, function( e ) {
						if( preview_elem.length && preview_elem.hasClass('focused') ) {
							// ignore anything happening when not focused
							var t = $(e.target);
							if( t.closest(preview_elem).length ) {
								// click inside preview_elem -> dont do anything
							} else if( t.closest(self).length ) {
								// click inside of widget containing element (this can happen on mouseup when a mousedown focused preview_elem ...) -> ignore
							} else if( $.contains(document.documentElement,e.target) ) {
								// click outside of overlay -> close()
								$(document).off(e);
								close_cb(c2o_elem);
								//e.stopPropagation();
								//e.preventDefault();
							}
						}
					} );
				}

				$('<input type="hidden" />')
					.attr( { name:opts.name, value:'' } )
					.appendTo(this);

				function setup_cb( c2o_elem, val, ovl ) {
					if( opts.hasOwnProperty('notes1_html') ) $(document.createElement('span')).attr({'class':'mj-click2edit-text-widget-overlay-notes1'}).html( opts.notes1_html ).appendTo(ovl);
					var ta_span = $(document.createElement('span')).attr({'class':'mj-click2edit-text-widget-overlay-inner'})
						.mjWidget( { type:'text', name:'_mjw'+opts.name, 'default':val } )
						.appendTo(ovl);
					if( opts.hasOwnProperty('notes2_html') ) $(document.createElement('span')).attr({'class':'mj-click2edit-text-widget-overlay-notes2'}).html( opts.notes2_html ).appendTo(ovl);
					window.setImmediate( function() {
						var ta = ta_span.find('textarea'),
							th = ta.height(),
							ih = ta[0].scrollHeight;
						if( ih > th ) ta.height(ih);
					});
				}
				function extract_cb(ovl) {
					var nv = ovl.find('.mj-click2edit-text-widget-overlay-inner').mjWidget('get');
					return nv;
				}
				function accept_cb(nv) {
					if( opts.hasOwnProperty('before_change') && opts.before_change ) opts.before_change.call(self[0],nv);
					_call_widget_method2(self[0],opts,'set',[opts,nv]);
					opts.change.call(self[0],nv);
				}
				function open_editor() {
					var val = _call_widget_method2(self[0],opts,'get',[opts]);
					var owe_opts = {'OverlayCssWidth':'auto','OkButtonText':mjt('save'),'CancelButtonText':mjt('cancel')};
					if( opts.hasOwnProperty('OffsetParent') ) owe_opts['OffsetParent'] = opts['OffsetParent'];
					//else owe_opts['OffsetParent'] = c2o_elem;
					if( opts.hasOwnProperty('OverlayCssClass') ) owe_opts['OverlayCssClass'] = opts['OverlayCssClass'];
					return mjWidgetOverlayEditor.open(c2o_elem,val,setup_cb,extract_cb,accept_cb,close_cb,owe_opts);
				}
				self
					.data('mj-widget-editor',open_editor)
					.on('click.mjWidget',function(e) {
						if( elementHasClassName(e.target,'mj-click2edit-text-widget-edit-link') ) {
							_inl_prev_blur(true);
							e.preventDefault();
							e.stopPropagation();
							window.setImmediate(open_editor);
						}
					} )
					.on('focusin.mjWidget','.mj-click2edit-text-widget-inline-preview', function(e) {
						window.setImmediate(_inl_prev_focus());
					} )
					.on('focusout.mjWidget','.mj-click2edit-text-widget-inline-preview', function(e) {
						close_cb(c2o_elem);
					} );
			},
			set: function( opts, value ) {
				mjWidgetOverlayEditor.close();
				var is_empty = opts.hasOwnProperty('is_empty') ? opts.is_empty(value) : value==='';
				value = is_empty ? '' : value;
				$('input[type=hidden]',this).val(value);
				var preview_elem = $('span.mj-click2edit-text-widget-inline-preview',this)
						.toggleClass('empty',is_empty)
						.html('<span class="mj-click2edit-text-widget-inline-preview-1">'+htmlspecialchars(value,false,true)+'</span>'), // tabindex="-1"
					preview_elem_link = $('span.mj-click2edit-text-widget-edit-link',this).detach().first();
				preview_elem_link.prependTo(this);
				if( !is_empty ) {
					preview_elem_link.clone()
						.removeAttr('id')
						.removeAttr('tabindex')
						.text(mjt('edit','!')+' …')
						.appendTo(preview_elem);
				}
			},
			get: function( opts ) {
				return $('input[type=hidden]',this).val();
			},
			focus: function( opts ) {
				var open_editor = $(this).data('mj-widget-editor');
				open_editor();
			},
			select: function( opts ) {
				var open_editor = $(this).data('mj-widget-editor');
				open_editor();
			},
		},

		'string-label': {
			init: function( opts ) {
				var elem = $( '<span />' ).attr( { id:opts.id, name:opts.name, 'class':'text string-label '+opts.addClass, 'tabindex':opts.focusable?0:-1 } ).appendTo(this);
				_mj_wdg_check_add_change( this, opts, elem );
			},
			get: function( opts ) {
				return $(this).data('mj-widget-value');
			},
			set: function( opts, value ) {
				$(this).data('mj-widget-value',value);
				$('span',this).first().text(value);
			},
			focus: function( opts ) {
				$('span',this).first().focus();
			},
			select: function( opts ) {
				$('span',this).first().select();
			},
		},

	};


	var _mj_entity_single_select_widgets_defaults = {
		set: function( opts, value ) {
			var self = $(this);
			// make sure, that entity list is loaded before setting the value ..
			_mj_query_entity_list_by_opts(
				opts,
				function _on_data_ready( data ) {
					//console.log({'fn':_on_data_ready,data,value});
					$('select',self).val(value);
				}
			);
		},
		get: function( opts ) {
			return $('select',this).val();
		},
	};

	widgets['user'] = Object.assign( {
		init: function( opts ) {
			opts.entity_source = opts.entity_source || {
				url: 'json/widgets/entities-users',
				query: opts.flt_role ? '&flt_role='+encodeURIComponent(opts.flt_role) : '',
			};
			_mj_init_wdg_entity_select(this,opts,{'width':'100%'});
		},
	}, _mj_entity_single_select_widgets_defaults );

	widgets['group'] = Object.assign( {
		init: function( opts ) {
			opts.entity_source = opts.entity_source || {
				url: 'json/widgets/entities-groups',
				query: opts.flt_role ? '&flt_role='+encodeURIComponent(opts.flt_role) : '',
			};
			_mj_init_wdg_entity_select(this,opts,{'width':'100%'});
		},
	}, _mj_entity_single_select_widgets_defaults );



	widgets['group_or_user'] = Object.assign( {
		init: function( opts ) {
			var self = $(this);

			opts.entity_source = opts.entity_source || {
				url: 'json/widgets/entities-groups_and_users',
				query: opts.flt_role ? '&flt_role='+encodeURIComponent(opts.flt_role) : '',
			};

			var select_opts = {};
			var elem = $(document.createElement('span')).attr({'class':'elem1'})
				.append( $('<select disabled><option>'+mjt('loading')+'</option></select>',{id:opts.id,name:opts.name}) ) // placeholder until entity list loaded
				.appendTo(self);

			_mj_query_entity_list_by_opts(
				opts,
				function _on_data_ready( data ) {
					if( opts.preProcessEntityData ) opts.preProcessEntityData(data);
					if( opts.setup_cb && !select_opts.setup_cb ) select_opts.setup_cb = opts.setup_cb;
					// build inner html of select with <optgroup> and <option> tags
					var i, g, u;
					var ug_opts = '';
					if( opts.add_empty_default ) {
						// add empty default
						ug_opts += '<option value=""></option>';
					}
					// add groups
					ug_opts += '<optgroup label="'+mjt('groups')+'">';
					for( i in data.groups ) {
						if( !data.groups.hasOwnProperty(i) ) continue;
						g = data.groups[i];
						ug_opts += '<option value="group.'+i+'">'+htmlspecialchars(g.name)+'</option>';
					}
					ug_opts += '</optgroup>';
					// add users
					ug_opts += '<optgroup label="'+mjt('users')+'">';
					for( i=0; i<data.users.length; ++i ) {
						u = data.users[i];
						ug_opts += '<option value="user.'+u[0]+'">'+htmlspecialchars(u[1])+'</option>';
					}
					ug_opts += '</optgroup>';
					// and pass into a 'list' widget
					elem.empty().mjWidget(
						Object.assign(
							{
								type: 'list',
								name: opts.name,
								id: opts.id,
								values: ug_opts,
								html_labels: true,
								allow_disabled_values: false,
								value: opts.value||'',
							},
							select_opts||{}
						)
					);
					_mj_wdg_check_add_change( self[0], opts, $('select',self) );
				}
			);
		},
	}, _mj_entity_single_select_widgets_defaults );



	widgets['category'] = Object.assign( {
		init: function( opts ) {
			opts.entity_source = opts.entity_source || {
				url: 'json/widgets/entities-categories',
				query: opts.tree_id ? '&tree_id='+encodeURIComponent(opts.tree_id) : '',
			};
			opts.preProcessEntityData = function( data ) {
				// add an entry for virtual 'other' category entity
				var eal = data.entities.length;
				if( eal===0 || (eal>0 && data.entities[eal-1].id!='0') ) {
					data.entities.push( {
						id:'0',
						title:mjt('category-other'),
						css: {
							'font-style':'italic',
						},
					} );
				}
			};
			var select_opts = {
				tree: true,
				select2_options: {
					showEntityIds: opts.show_ids,
					dropdownAutoWidth: opts.autoWidth!=undefined ? opts.autoWidth : false,
				},
			};
			opts.show_ids = false; // NOTE: this is already actived by the showEntityIds option
			_mj_init_wdg_entity_select(this,opts,select_opts);
		},
	}, _mj_entity_single_select_widgets_defaults );


	widgets['meta-field'] = Object.assign( {
		init: function( opts ) {
			var self = $(this);

			opts.entity_source = opts.entity_source || {
				cache_key: 'md-meta-module',
				query_data_cb: function _on_query_data( query_success_cb ) {
					MDMeta.queryGroupedMetaDefs( function( meta_grouped, meta_defs ) {
						query_success_cb( { meta_grouped:meta_grouped, meta_defs:meta_defs } );
					} );
				},
			};

			var select_opts = {};
			var elem = $(document.createElement('span')).attr({'class':'elem1'})
				.append( $('<select disabled><option>'+mjt('loading')+'</option></select>',{id:opts.id,name:opts.name}) ) // placeholder until entity list loaded
				.appendTo(self);

			_mj_query_entity_list_by_opts(
				opts,
				function _on_data_ready( data ) {
					if( opts.preProcessEntityData ) opts.preProcessEntityData(data);
					if( opts.setup_cb && !select_opts.setup_cb ) select_opts.setup_cb = opts.setup_cb;
					// build inner html of select with <optgroup> and <option> tags
					var g, group, m, meta_id, md, spec, html=[], defs_html;
					for( g=0; g<data.meta_grouped.length; ++g ) {
						group = data.meta_grouped[g];
						defs_html = [];
						for( m=0; m<group.defs.length; ++m ) {
							meta_id = group.defs[m];
							if( data.meta_defs.hasOwnProperty(meta_id) ) {
								md = data.meta_defs[meta_id];
								if( opts.flt_cb && !opts.flt_cb(meta_id,md) ) continue; // skip this one
								spec = meta_id + ' ' + htmlspecialchars(md.lname)+ ' ('+MDMeta.getTypeName(md.type,md.list==1)+')';
								defs_html.push('<option value="'+meta_id+'">'+spec+'</option>');
							}
						}
						if( defs_html.length!==0 ) html.push('<optgroup label="'+htmlspecialchars(group.lname,true,true)+'">'+defs_html.join('')+'</optgroup>');
					}
					// and pass into a 'list' widget
					elem.empty().mjWidget(
						Object.assign(
							{
								type: 'list',
								name: opts.name,
								id: opts.id,
								values: html.join(''),
								html_labels: true,
								allow_disabled_values: false,
								value: opts.value||'',
							},
							select_opts||{}
						)
					);
					_mj_wdg_check_add_change( self[0], opts, $('select',self) );
				}
			);
		},
	}, _mj_entity_single_select_widgets_defaults );




	var _mj_entity_list_select_widgets_defaults = {
		set: function( opts, value ) {
			_mj_init_wdg_entity_list(this,opts,_mj_wdg_clean_id_list(value),{'width':'500px'});
		},
		get: function( opts ) {
			return $(this).data('mj-widget-value');
		},
	};

	widgets['group_list'] = Object.assign( {
		init: function( opts ) {
			opts.entity_source = opts.entity_source || {
				url: 'json/widgets/entities-groups',
				query: opts.flt_role ? '&flt_role='+encodeURIComponent(opts.flt_role) : '',
			};
			opts.hide_exclusions = true;
			_mj_init_wdg_entity_list(this,opts,[],{'width':'500px'});
		},
	}, _mj_entity_list_select_widgets_defaults );

	widgets['user_list'] = Object.assign( {
		init: function( opts ) {
			opts.entity_source = opts.entity_source || {
				url: 'json/widgets/entities-users',
				query: opts.flt_role ? '&flt_role='+encodeURIComponent(opts.flt_role) : '',
			};
			opts.hide_exclusions = true;
			_mj_init_wdg_entity_list(this,opts,[],{'width':'500px'});
		},
	}, _mj_entity_list_select_widgets_defaults );

	widgets['category_list'] = Object.assign( {
		init: function( opts ) {
			opts.entity_source = opts.entity_source || {
				url: 'json/widgets/entities-categories',
				query: opts.tree_id ? '&tree_id='+encodeURIComponent(opts.tree_id) : '',
			};
			opts.hide_exclusions = false;
			_mj_init_wdg_entity_list(this,opts,[],{'width':'500px'});
		},
	}, _mj_entity_list_select_widgets_defaults );





	// default methods for all types
	var default_methods = {
		focus: function( opts ) {
			$('input[type!="hidden"],textarea,select',this).first().focus();
		},
		select: function( opts ) {
			$('input[type="text"],textarea,select',this).first().select();
		},
		set: function( opts, value ) {
			$('input[type="text"],textarea,select',this).first().val(value);
		},
		/*, too slow: use specialized get() per type...
		get: function( opts ) {
			return $('input[type="text"],textarea,select',this).first().val();
		}*/
	};

	function _define_widget_methods( opts, type ) {
		if( !widgets[type] ) throw ('Unknown mjWidget type "'+type+'" (while initializing it)');
		var widget = widgets[type];
		if( !widget._defaults_merged ) {
			for( var d in default_methods ) {
				if( !default_methods.hasOwnProperty(d) ) continue;
				if( !widget.hasOwnProperty(d) ) widget[d] = default_methods[d];
			}
			widget._defaults_merged = true;
		}
		opts._widget_methods = widget;
	}

	function _call_widget_method2( self, opts, func, args ) {
		if( !opts._widget_methods ) throw new Error('mjWidget of type "'+opts.type+'" is not quite initialized (while trying to call function "'+func+'" on it)');
		if( !opts._widget_methods[func] ) throw new Error('mjWidget type "'+opts.type+'" has no function "'+func+'"');
		return opts._widget_methods[func].apply(self,args);
	}

	var methods = {
		init: function( opts ) {
			if( !opts.type ) throw new Error('No widget type specified');
			_define_widget_methods(opts,opts.type);

			if( !opts.hasOwnProperty('id') || opts.id===null ) opts.id = _mj_wdg_id();
			if( !opts.hasOwnProperty('name') || opts.name===null ) opts.name = opts.id;
			if( !opts.hasOwnProperty('addClass') ) opts.addClass = '';

			if( !('function' === typeof opts.change) ) opts.change = function() { }; // simplifies type-specific code

			// if no value, but a default option exist, then set value to default
			if( !opts.hasOwnProperty('value') && opts.hasOwnProperty('default') ) opts.value = opts['default'];

			$(this).data('mj-widget',opts);
			_call_widget_method2(this,opts,'init',arguments);

			if( opts.hasOwnProperty('value') ) {
				opts._widget_methods.set.call(this,opts,opts.value);
			} else if( opts.hasOwnProperty('default') ) {
				opts._widget_methods.set.call(this,opts,opts['default']);
			}
		},
		destroy: function( opts ) {
			$(this).empty().removeData(['mj-widget','mj-widget-value','mj-widget-editor']);
		},
		id: function( opts ) {
			return opts.hasOwnProperty('id') ? opts.id : null;
		},
		focus_id: function( opts ) {
			if( opts.hasOwnProperty('_widget_focus_id') ) return opts._widget_focus_id;
			return opts.hasOwnProperty('id') ? opts.id : null;
		},
		name: function( opts ) {
			return opts.hasOwnProperty('name') ? opts.name : null;
		},
		trigger: function( opts ) {
			if( opts.hasOwnProperty('change') ) opts.change.call(this);
		},
		set: 'pass',
		get: 'pass',
		focus: 'pass',
		select: 'pass',
		add: 'pass',
		replace: 'pass',
	};

	$.fn.mjWidget = function mjWidget( arg ) {
		// -> "this" is a jQuery object
		var args=arguments, rv;
		if( typeof arg === 'object' || !arg ) {
			this.each( function mjWidget1_init() {
				// -> "this" is a DOMElement
				methods.init.apply(this,args);
			} );
			return this;
		} else if( methods[arg] ) {
			var method = methods[arg];
			var item_args = Array.prototype.slice.call(args);
			this.each( function mjWidget1_other() {
				// -> "this" is a DOMElement
				item_args[0] = $(this).data('mj-widget');
				if( item_args[0]===undefined && arg!=='destroy' ) throw new Error('Element was not (yet) initialized as an mjWidget');
				rv = ( method==='pass' ? item_args[0]._widget_methods[arg] : method ).apply(this,item_args);
			} );
			return rv===undefined ? this : rv;
		}
		throw new Error('Method '+arg+' does not exist on jQuery.mjWidget');
	};

})(jQuery,mj);
